import { z } from "zod";
import { PermissionSchema } from "./permissions";

/**
 * Slugs for the two global default roles that exist in every environment.
 * The `roles.role_slug` column stays free-form varchar (custom org roles use
 * arbitrary slugs); this enum only names the built-in defaults.
 */
export enum DefaultRoleSlug {
  ADMIN = "admin",
  USER = "user",
}

/**
 * A role as exposed to clients for the role picker in team management.
 * `organizationId` is null for the global default roles and set for org-scoped
 * custom roles, letting clients disambiguate a global default from a custom
 * role that reuses the same slug.
 */
export const RoleSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  roleSlug: z.string(),
  name: z.string(),
});

export type Role = z.infer<typeof RoleSchema>;

/**
 * A role with its granted permissions, as exposed to role management. Default
 * roles (`organizationId` null) are immutable; custom roles are org-scoped.
 */
export const RoleWithPermissionsSchema = z.object({
  id: z.string(),
  organizationId: z.string().nullable(),
  roleSlug: z.string(),
  name: z.string(),
  permissions: z.array(z.string()),
  isDefault: z.boolean(),
});

export type RoleWithPermissions = z.infer<typeof RoleWithPermissionsSchema>;

/** Response payload for GET /roles: the roles available in the organization. */
export const RolesResponseSchema = z.object({
  roles: z.array(RoleWithPermissionsSchema),
});

export type RolesResponse = z.infer<typeof RolesResponseSchema>;

/** Request body for POST /roles: create a custom role with its permissions. */
export const CreateRoleRequestSchema = z.object({
  name: z.string().min(1),
  permissions: z.array(PermissionSchema),
});

export type CreateRoleRequest = z.infer<typeof CreateRoleRequestSchema>;

/** Request body for PUT /roles/:roleId: rename and/or replace permissions. */
export const UpdateRoleRequestSchema = z.object({
  name: z.string().min(1).optional(),
  permissions: z.array(PermissionSchema).optional(),
});

export type UpdateRoleRequest = z.infer<typeof UpdateRoleRequestSchema>;

/** A permission primitive described for the role editor UI. */
export const PermissionCatalogEntrySchema = z.object({
  slug: z.string(),
  label: z.string(),
  group: z.string(),
});

export type PermissionCatalogEntry = z.infer<
  typeof PermissionCatalogEntrySchema
>;
